type ScrollBehaviorOption = "auto" | "smooth";

function getKeyboardInset(): number {
  if (window.visualViewport) return 0;
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue("--keyboard-inset");
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getVisibleBounds(element: HTMLElement): {
  top: number;
  bottom: number;
  scrollContainer: HTMLElement | null;
} {
  const visualViewport = window.visualViewport;
  let top = visualViewport?.offsetTop ?? 0;
  let bottom = visualViewport
    ? top + visualViewport.height
    : window.innerHeight - getKeyboardInset();

  let ancestor = element.parentElement;
  let scrollContainer: HTMLElement | null = null;
  while (ancestor) {
    const overflowY = window.getComputedStyle(ancestor).overflowY;
    if (/(auto|scroll|overlay)/.test(overflowY)) {
      const ancestorRect = ancestor.getBoundingClientRect();
      top = Math.max(top, ancestorRect.top);
      bottom = Math.min(bottom, ancestorRect.bottom);
      scrollContainer = ancestor;
      break;
    }
    ancestor = ancestor.parentElement;
  }

  return { top, bottom, scrollContainer };
}

/**
 * `window.innerHeight` includes the fixed chat composer and can include the
 * area hidden by an iOS keyboard. Measure against the nearest real scrolling
 * viewport instead, then ask the browser for the smallest useful movement.
 */
export function scrollIntoNearestViewport(
  element: HTMLElement,
  behavior: ScrollBehaviorOption
): boolean {
  if (typeof element.scrollIntoView !== "function") return false;

  const rect = element.getBoundingClientRect();
  const bounds = getVisibleBounds(element);
  // The active panel is nested inside the action card's padding. Respect its
  // CSS scroll margin so scrolling the controls into view also preserves that
  // final bit of card chrome instead of landing the last row flush against the
  // composer. This uses existing scrollable padding rather than adding a tall
  // transcript spacer.
  const rawBottomMargin = Number.parseFloat(
    window.getComputedStyle(element).scrollMarginBottom
  );
  const bottomMargin = Number.isFinite(rawBottomMargin)
    ? Math.max(rawBottomMargin, 0)
    : 0;

  if (rect.top >= bounds.top && rect.bottom + bottomMargin <= bounds.bottom) {
    return false;
  }

  const scrollDelta =
    rect.bottom + bottomMargin > bounds.bottom
      ? rect.bottom + bottomMargin - bounds.bottom
      : rect.top - bounds.top;
  if (
    bounds.scrollContainer &&
    typeof bounds.scrollContainer.scrollTo === "function"
  ) {
    bounds.scrollContainer.scrollTo({
      top: bounds.scrollContainer.scrollTop + scrollDelta,
      behavior,
    });
    return true;
  }

  element.scrollIntoView({ behavior, block: "nearest" });
  return true;
}
